import { useState, useMemo } from "react";
import {
  useAdminExamPlans,
  useCreateAdminExamPlan,
  useUpdateAdminExamPlan,
  useDeleteAdminExamPlan,
  useAdminApiProviders,
} from "../../../api/admin";
import { toast } from "react-hot-toast";

// Backend response types
interface BackendExamPlan {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  amount: string;
  exam_id: string;
  is_active: boolean;
  commission: string | null;
  provider: number;
}

interface BackendProvider {
  id: number;
  name: string;
  is_active: boolean;
}

interface ExamPlan {
  id: number;
  name: string;
  amount: number;
  examId: string;
  isActive: boolean;
  commission: number | null;
  provider: number;
  providerName: string;
  createdAt: string;
}

export default function ExamPinsManagement() {
  // API Hooks
  const {
    data: examPlansData,
    isLoading: plansLoading,
    refetch: refetchPlans,
  } = useAdminExamPlans();
  const { data: providersResponse } = useAdminApiProviders();

  const createPlan = useCreateAdminExamPlan();
  const updatePlan = useUpdateAdminExamPlan();
  const deletePlan = useDeleteAdminExamPlan();

  // Local State
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<ExamPlan | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    amount: "",
    exam_id: "",
    commission: "",
    provider: "",
    is_active: true,
  });

  // Extract providers from nested response
  const providers = useMemo(() => {
    if (!providersResponse?.results?.data) return [];
    return providersResponse.results.data as BackendProvider[];
  }, [providersResponse]);

  // Map exam plans
  const examPlans = useMemo(() => {
    if (!examPlansData || !Array.isArray(examPlansData)) return [];

    return (examPlansData as BackendExamPlan[]).map((plan) => {
      const provider = providers.find((p) => p.id === plan.provider);

      return {
        id: plan.id,
        name: plan.name,
        amount: parseFloat(plan.amount),
        examId: plan.exam_id,
        isActive: plan.is_active,
        commission: plan.commission ? parseFloat(plan.commission) : null,
        provider: plan.provider,
        providerName: provider?.name || `Provider ${plan.provider}`,
        createdAt: new Date(plan.created_at).toLocaleDateString(),
      };
    });
  }, [examPlansData, providers]);

  // Filter plans
  const filteredPlans = useMemo(() => {
    return examPlans.filter(
      (plan) =>
        plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.examId.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [examPlans, searchTerm]);

  // CRUD handlers
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload = {
        name: formData.name,
        amount: formData.amount,
        exam_id: formData.exam_id,
        commission: formData.commission || null,
        provider: parseInt(formData.provider),
        is_active: formData.is_active,
      };

      if (editingPlan) {
        await updatePlan.mutateAsync({
          id: editingPlan.id,
          data: payload,
        });
        toast.success("Exam plan updated successfully!");
      } else {
        await createPlan.mutateAsync(payload);
        toast.success("Exam plan created successfully!");
      }

      refetchPlans();
      resetForm();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save exam plan";
      toast.error(errorMessage);
      console.error("Error saving exam plan:", error);
    }
  };

  const handleEdit = (plan: ExamPlan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      amount: plan.amount.toString(),
      exam_id: plan.examId,
      commission: plan.commission?.toString() || "",
      provider: plan.provider.toString(),
      is_active: plan.isActive,
    });
    setShowModal(true);
  };

  const handleDelete = async (planId: number) => {
    if (!confirm("Are you sure you want to delete this exam plan?")) return;

    try {
      await deletePlan.mutateAsync(planId);
      toast.success("Exam plan deleted successfully!");
      refetchPlans();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete exam plan";
      toast.error(errorMessage);
      console.error("Error deleting exam plan:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      amount: "",
      exam_id: "",
      commission: "",
      provider: "",
      is_active: true,
    });
    setEditingPlan(null);
    setShowModal(false);
  };

  // Loading state
  if (plansLoading) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-red-600 animate-spin"></i>
            <p className="mt-2 text-gray-600">Loading exam plans...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Exam Pins Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage examination result checker pins and pricing
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="mt-4 md:mt-0 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-fill mr-2"></i>
          Add Exam Plan
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Exam Plans
          </label>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by exam name or ID"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Empty State */}
      {filteredPlans.length === 0 && (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
          <i className="ri-file-list-3-line text-5xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Exam Plans Found
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm
              ? "Try adjusting your search terms"
              : "Get started by adding your first exam plan"}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <i className="ri-add-fill mr-2"></i>
              Add Exam Plan
            </button>
          )}
        </div>
      )}

      {/* Exam Plans Grid */}
      {filteredPlans.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPlans.map((plan) => (
            <div
              key={plan.id}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <i className="ri-file-list-3-fill text-indigo-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{plan.name}</h3>
                    <p className="text-sm text-gray-500">{plan.providerName}</p>
                  </div>
                </div>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    plan.isActive
                      ? "bg-purple-100 text-purple-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {plan.isActive ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Exam ID (API):</span>
                  <span className="font-medium bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                    {plan.examId}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Amount:</span>
                  <span className="font-semibold text-gray-900">
                    ₦{plan.amount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Commission:</span>
                  <span className="font-medium">
                    {plan.commission ? `${plan.commission}%` : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Plan ID:</span>
                  <span className="font-medium">#{plan.id}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Created:</span>
                  <span className="font-medium">{plan.createdAt}</span>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(plan)}
                  className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-edit-fill mr-1"></i>
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(plan.id)}
                  className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-delete-bin-fill mr-1"></i>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingPlan ? "Edit Exam Plan" : "Add New Exam Plan"}
                </h3>
                <button
                  onClick={resetForm}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-close-fill text-xl"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exam Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="e.g., WAEC, NECO, NABTEB"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exam ID{" "}
                    <span className="text-gray-500">(from API provider)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.exam_id}
                    onChange={(e) =>
                      setFormData({ ...formData, exam_id: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="e.g., 1"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    This is the unique exam ID from your API provider
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Provider
                  </label>
                  <select
                    value={formData.provider}
                    onChange={(e) =>
                      setFormData({ ...formData, provider: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    required
                  >
                    <option value="">Select Provider</option>
                    {providers.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount (₦)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) =>
                        setFormData({ ...formData, amount: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Commission (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.commission}
                      onChange={(e) =>
                        setFormData({ ...formData, commission: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <label
                    htmlFor="is_active"
                    className="text-sm font-medium text-gray-700"
                  >
                    Active Plan
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={createPlan.isPending || updatePlan.isPending}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createPlan.isPending || updatePlan.isPending ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        {editingPlan ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      <>{editingPlan ? "Update Plan" : "Create Plan"}</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
